import { Flex, Radio, RadioGroup, Stack, Text } from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';
import type { CreditsPurchaseConfig } from '../utils/credits-purchase-config';

export type CreditsPurchasePackPickerProps = {
  packs: NonNullable<CreditsPurchaseConfig['packs']>;
  packNoun: string;
  unitLabel: string;
  value: number;
  isDisabled: boolean;
  onChange: (size: number) => void;
};

/**
 * The fixed-pack picker: one radio row per pack showing the pack size, the effective per-unit price,
 * and the pack price. Shared by the Stripe and Ordway purchase forms, which differ in how the purchase
 * is billed but offer the identical packs.
 */
export function CreditsPurchasePackPicker({
  packs,
  packNoun,
  unitLabel,
  value,
  isDisabled,
  onChange,
}: CreditsPurchasePackPickerProps) {
  return (
    <RadioGroup
      ariaLabel={`${packNoun} packages`}
      name="credits-purchase-pack"
      value={String(value)}
      onChange={(selected) => onChange(Number(selected))}
      isDisabled={isDisabled}
    >
      <Stack gap="100" data-test-credits-pack-picker>
        {packs.map((pack) => {
          const perUnit = pack.priceCents / pack.size;
          return (
            <Flex key={pack.size} alignItems="center" justifyContent="space-between" gap="200">
              <Radio
                value={String(pack.size)}
                label={`+${formatNumber(pack.size)} ${packNoun}`}
                description={`$${(perUnit / 100).toFixed(2)} per ${singularUnit(unitLabel)}`}
                data-test-credits-pack={pack.size}
              />
              <Text weight="semibold">{formatCentsToUsd(pack.priceCents)}</Text>
            </Flex>
          );
        })}
      </Stack>
    </RadioGroup>
  );
}

/** The pack noun for a config: its explicit `packNoun`, else its unit label in title case. */
export function packNounFor(config: CreditsPurchaseConfig): string {
  return config.packNoun ?? capitalizeUnit(config.creditsUnitLabel ?? 'credits');
}

function capitalizeUnit(unitLabel: string): string {
  return unitLabel
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function singularUnit(unitLabel: string): string {
  return unitLabel.endsWith('s') ? unitLabel.slice(0, -1) : unitLabel;
}
